import React, { useState, useEffect, useCallback } from 'react';
import {
  KeyRound,
  Plus,
  Search,
  Folder,
  Lock,
  Unlock,
  Globe,
  User,
  Eye,
  EyeOff,
  Copy,
  Edit,
  Trash2,
  FolderPlus,
  Users,
  Clock,
  ExternalLink,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { VaultItem, VaultFolder } from '@/models/vault.model';
import {
  subscribeToVaultItems,
  subscribeToVaultFolders,
  decryptVaultItemPassword,
  deleteVaultItem,
  createVaultFolder,
  deleteVaultFolder,
} from '@/services/vault.service';
import {
  isVaultUnlocked,
  unlockVaultWithPassphrase,
  lockVaultFull,
  getSessionTimeRemaining,
} from '@/services/vault-crypto.service';
import { VaultItemForm } from './vault/VaultItemForm';
import { VaultItemDetail } from './vault/VaultItemDetail';

export const PasswordVault: React.FC = () => {
  const { profile } = useAuth();
  const [items, setItems] = useState<VaultItem[]>([]);
  const [folders, setFolders] = useState<VaultFolder[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [showTeamOnly, setShowTeamOnly] = useState(false);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [loading, setLoading] = useState(true);
  const [sessionTimeLeft, setSessionTimeLeft] = useState(0);

  // Modal states
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState<VaultItem | null>(null);
  const [viewingItem, setViewingItem] = useState<VaultItem | null>(null);
  const [showFolderForm, setShowFolderForm] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderIsTeam, setNewFolderIsTeam] = useState(false);

  // Check vault status
  useEffect(() => {
    setIsUnlocked(isVaultUnlocked());
    setLoading(false);
  }, []);

  // Session timer
  useEffect(() => {
    if (!isUnlocked) return;

    const interval = setInterval(() => {
      const remaining = getSessionTimeRemaining();
      setSessionTimeLeft(remaining);
      if (remaining === 0) {
        setIsUnlocked(false);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [isUnlocked]);

  // Subscribe to items and folders
  useEffect(() => {
    if (!isUnlocked || !profile?.id) return;

    const unsubItems = subscribeToVaultItems(profile.id, true, setItems);
    const unsubFolders = subscribeToVaultFolders(profile.id, true, setFolders);

    return () => {
      unsubItems();
      unsubFolders();
    };
  }, [isUnlocked, profile?.id]);

  const handleUnlock = async () => {
    if (!passphrase) {
      setUnlockError('Please enter your passphrase');
      return;
    }

    const success = await unlockVaultWithPassphrase(passphrase);
    if (success) {
      setIsUnlocked(true);
      setPassphrase('');
      setUnlockError('');
    } else {
      setUnlockError('Failed to unlock vault. Please try again.');
    }
  };

  const handleLock = () => {
    lockVaultFull();
    setIsUnlocked(false);
    setItems([]);
    setFolders([]);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !profile?.id) return;

    await createVaultFolder({
      name: newFolderName.trim(),
      isTeam: newFolderIsTeam,
      createdBy: profile.id,
    });

    setNewFolderName('');
    setNewFolderIsTeam(false);
    setShowFolderForm(false);
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!confirm('Delete this folder? Items will be moved to "No Folder".')) return;
    await deleteVaultFolder(folderId);
  };

  const handleDeleteItem = async (itemId: string) => {
    if (!confirm('Delete this password? This cannot be undone.')) return;
    await deleteVaultItem(itemId);
  };

  const formatTimeRemaining = (ms: number) => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Filter items
  const filteredItems = items.filter((item) => {
    const matchesSearch =
      !searchQuery ||
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.url?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFolder =
      selectedFolder === null || item.folderId === selectedFolder;

    const matchesTeamFilter = !showTeamOnly || item.isTeam;

    return matchesSearch && matchesFolder && matchesTeamFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Unlock screen
  if (!isUnlocked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-md mx-auto px-4 py-20">
          <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
            <div className="text-center mb-6">
              <div className="inline-flex p-4 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full mb-4">
                <Lock className="w-8 h-8 text-slate-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Password Vault</h1>
              <p className="text-gray-600 mt-2">
                Enter your master passphrase to unlock
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Master Passphrase
                </label>
                <input
                  type="password"
                  value={passphrase}
                  onChange={(e) => setPassphrase(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleUnlock()}
                  placeholder="Enter your passphrase"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {unlockError && (
                <p className="text-sm text-red-600">{unlockError}</p>
              )}

              <button
                onClick={handleUnlock}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
              >
                <Unlock className="w-4 h-4" />
                Unlock Vault
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Your passphrase is never stored. It encrypts all your passwords locally.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-slate-600 to-slate-700 rounded-lg">
              <KeyRound className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Password Vault</h1>
              <p className="text-sm text-gray-600">
                Securely store and manage credentials
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock className="w-4 h-4" />
              <span>Session: {formatTimeRemaining(sessionTimeLeft)}</span>
            </div>
            <button
              onClick={handleLock}
              className="px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-2"
            >
              <Lock className="w-4 h-4" />
              Lock
            </button>
            <button
              onClick={() => {
                setEditingItem(null);
                setShowItemForm(true);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add Password
            </button>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar - Folders */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-gray-900">Folders</h2>
                <button
                  onClick={() => setShowFolderForm(true)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                  title="New Folder"
                >
                  <FolderPlus className="w-4 h-4 text-gray-600" />
                </button>
              </div>

              <div className="space-y-1">
                <button
                  onClick={() => setSelectedFolder(null)}
                  className={`w-full text-left px-3 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                    selectedFolder === null
                      ? 'bg-blue-50 text-blue-700'
                      : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <Folder className="w-4 h-4" />
                  All Items
                  <span className="ml-auto text-xs text-gray-500">
                    {items.length}
                  </span>
                </button>

                {folders.map((folder) => (
                  <div
                    key={folder.id}
                    className={`group flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                      selectedFolder === folder.id
                        ? 'bg-blue-50 text-blue-700'
                        : 'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <button
                      onClick={() => setSelectedFolder(folder.id)}
                      className="flex-1 text-left flex items-center gap-2"
                    >
                      {folder.isTeam ? (
                        <Users className="w-4 h-4" />
                      ) : (
                        <Folder className="w-4 h-4" />
                      )}
                      <span className="truncate">{folder.name}</span>
                    </button>
                    <button
                      onClick={() => handleDeleteFolder(folder.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded transition-all"
                    >
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={showTeamOnly}
                    onChange={(e) => setShowTeamOnly(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <Users className="w-4 h-4" />
                  Team passwords only
                </label>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search passwords..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Items List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              {filteredItems.length === 0 ? (
                <div className="p-8 text-center">
                  <KeyRound className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">No passwords found</p>
                  <button
                    onClick={() => {
                      setEditingItem(null);
                      setShowItemForm(true);
                    }}
                    className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Add your first password
                  </button>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredItems.map((item) => (
                    <VaultItemRow
                      key={item.id}
                      item={item}
                      onView={() => setViewingItem(item)}
                      onEdit={() => {
                        setEditingItem(item);
                        setShowItemForm(true);
                      }}
                      onDelete={() => handleDeleteItem(item.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Item Form Modal */}
      {showItemForm && (
        <VaultItemForm
          item={editingItem}
          folders={folders}
          userId={profile?.id || ''}
          onClose={() => {
            setShowItemForm(false);
            setEditingItem(null);
          }}
        />
      )}

      {/* Item Detail Modal */}
      {viewingItem && (
        <VaultItemDetail
          item={viewingItem}
          folders={folders}
          onClose={() => setViewingItem(null)}
          onEdit={() => {
            setEditingItem(viewingItem);
            setViewingItem(null);
            setShowItemForm(true);
          }}
        />
      )}

      {/* New Folder Modal */}
      {showFolderForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold mb-4">New Folder</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Folder Name
                </label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="e.g., Work, Personal, Servers"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newFolderIsTeam}
                  onChange={(e) => setNewFolderIsTeam(e.target.checked)}
                  className="rounded border-gray-300"
                />
                <Users className="w-4 h-4 text-gray-600" />
                <span className="text-sm text-gray-700">
                  Team folder (visible to all team members)
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowFolderForm(false);
                  setNewFolderName('');
                  setNewFolderIsTeam(false);
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Create Folder
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Vault Item Row Component
interface VaultItemRowProps {
  item: VaultItem;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const VaultItemRow: React.FC<VaultItemRowProps> = ({
  item,
  onView,
  onEdit,
  onDelete,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [password, setPassword] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);

  const handleRevealPassword = async () => {
    if (showPassword) {
      setShowPassword(false);
      setPassword(null);
      return;
    }

    try {
      const decrypted = await decryptVaultItemPassword(item);
      setPassword(decrypted);
      setShowPassword(true);

      // Auto-hide after 10 seconds
      setTimeout(() => {
        setShowPassword(false);
        setPassword(null);
      }, 10000);
    } catch (error) {
      console.error('Failed to decrypt password:', error);
    }
  };

  const handleCopyPassword = async () => {
    try {
      const decrypted = password || (await decryptVaultItemPassword(item));
      await navigator.clipboard.writeText(decrypted);
      setCopying(true);
      setTimeout(() => setCopying(false), 2000);
    } catch (error) {
      console.error('Failed to copy password:', error);
    }
  };

  return (
    <div className="p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3
              className="font-medium text-gray-900 truncate cursor-pointer hover:text-blue-600"
              onClick={onView}
            >
              {item.title}
            </h3>
            {item.isTeam && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                <Users className="w-3 h-3" />
                Team
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
            {item.username && (
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {item.username}
              </span>
            )}
            {item.url && (
              <a
                href={item.url.startsWith('http') ? item.url : `https://${item.url}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 hover:text-blue-600 truncate max-w-[200px]"
              >
                <Globe className="w-3 h-3" />
                {item.url.replace(/^https?:\/\//, '')}
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>

        {/* Password field */}
        <div className="flex items-center gap-2">
          <div className="w-32 font-mono text-sm bg-gray-100 px-3 py-1 rounded">
            {showPassword && password ? password : '••••••••'}
          </div>
          <button
            onClick={handleRevealPassword}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? (
              <EyeOff className="w-4 h-4 text-gray-600" />
            ) : (
              <Eye className="w-4 h-4 text-gray-600" />
            )}
          </button>
          <button
            onClick={handleCopyPassword}
            className={`p-2 rounded transition-colors ${
              copying
                ? 'bg-green-100 text-green-600'
                : 'hover:bg-gray-200 text-gray-600'
            }`}
            title="Copy password"
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="p-2 hover:bg-gray-200 rounded transition-colors"
            title="Edit"
          >
            <Edit className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 hover:bg-red-100 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>
    </div>
  );
};
